/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import VideoStopOverlayIcon from '@atlaskit/icon/core/video-stop-overlay';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoThinkingIndicator } from '@atlassian/conversation-assistant-ui-components';

import {
	PageContextType,
	usePageContextType,
} from '../../../../common/ui/chat-input-action-buttons/current-page-as-context';
import type { InlineChatTrace } from '../../../../controllers/inline-chat-controller';

import messages from './messages';

const styles = cssMap({
	escape: {
		paddingInline: token('space.050'),
		paddingBlock: token('space.025'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
		color: token('color.text.subtlest'),
		borderRadius: token('radius.small'),
		font: token('font.body.small'),
	},

	thinkingContainer: {
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},

	thinkingText: {
		color: token('color.text.subtle'),
	},
});

type ThinkingViewProps = {
	latestTrace: InlineChatTrace | null;
	onCancel: () => void;
	onFollow?: () => void;
};

export const ThinkingView = ({ latestTrace, onCancel, onFollow }: ThinkingViewProps) => {
	di(usePageContextType);
	const { formatMessage } = useIntl();
	const pageContextType = usePageContextType();
	const isNotWhiteboardContext = pageContextType !== PageContextType.CONFLUENCE_WHITEBOARD;

	return (
		<>
			<Box xcss={styles.thinkingContainer}>
				<RovoThinkingIndicator title={formatMessage(latestTraceToI18n(latestTrace))} />
			</Box>

			<Inline alignBlock="center" space="space.100">
				{onFollow && (
					<Button appearance="subtle" onClick={onFollow}>
						{formatMessage(messages.followAiMateButton)}
					</Button>
				)}
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				{isNotWhiteboardContext && <Box xcss={styles.escape}>Esc</Box>}
				<IconButton
					isTooltipDisabled={false}
					shape="circle"
					onClick={onCancel}
					icon={(iconProps) => (
						<VideoStopOverlayIcon {...iconProps} color={token('color.icon.danger')} label={''} />
					)}
					label={formatMessage(messages.stopGeneratingButtonLabel)}
				/>
			</Inline>
		</>
	);
};

const latestTraceToI18n = (latestTrace: InlineChatTrace | null) => {
	if (
		latestTrace?.message_template === 'ACTION_CLASSIFICATION' &&
		(latestTrace?.action_key === 'aifc-adf-update' ||
			latestTrace?.action_key === 'aifc-confluence-whiteboard-update')
	) {
		return messages.editingText;
	}
	return messages.thinkingText;
};
